import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const TYPES = [
  { v: "entry", label: "Entrada (compra/restock)" },
  { v: "exit", label: "Salida (merma/devolución)" },
  { v: "adjustment", label: "Ajuste (+/-)" },
  { v: "sale", label: "Venta" },
];

const TIERS = [
  { v: "min", label: "Precio mínimo", get: (p) => p.min_price },
  { v: "reseller", label: "Reventa", get: (p) => p.reseller_price },
  { v: "marketplace", label: "Distribuidor", get: (p) => p.marketplace_price },
  { v: "store", label: "Tienda", get: (p) => p.store_price },
  { v: "custom", label: "Personalizado", get: () => 0 },
];

export default function MovementForm({ product, onClose, onDone }) {
  const [type, setType] = useState("entry");
  const [quantity, setQuantity] = useState(1);
  const [tier, setTier] = useState("store");
  const [salePrice, setSalePrice] = useState(product.store_price);
  const [saleCurrency, setSaleCurrency] = useState(product.sale_currency || product.currency || "MXN");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleTier = (v) => {
    setTier(v);
    const t = TIERS.find((x) => x.v === v);
    if (t && v !== "custom") setSalePrice(t.get(product));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        product_id: product.id,
        type,
        quantity: parseInt(quantity),
        note,
      };
      if (type === "sale") {
        payload.sale_price = parseFloat(salePrice) || 0;
        payload.sale_currency = saleCurrency;
        payload.price_tier = tier;
      }
      await api.post("/movements", payload);
      toast.success("Movimiento registrado");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="movement-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Movimiento</DialogTitle>
          <div className="text-sm text-zinc-500">{product.name} · Stock actual: <span className="numeric font-semibold">{product.stock}</span></div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Tipo</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1" data-testid="mov-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {type === "adjustment" && <p className="text-xs text-zinc-500 mt-1">Usa negativos para restar (ej: -2)</p>}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Cantidad</Label>
            <Input type="number" value={quantity} onChange={(e)=>setQuantity(e.target.value)} required data-testid="mov-qty" className="mt-1 numeric" />
          </div>
          {type === "sale" && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tier de precio</Label>
                <Select value={tier} onValueChange={handleTier}>
                  <SelectTrigger className="mt-1" data-testid="mov-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIERS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Precio venta</Label>
                  <Input type="number" step="0.01" value={salePrice} onChange={(e)=>setSalePrice(e.target.value)} data-testid="mov-price" className="mt-1 numeric" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Moneda</Label>
                  <Select value={saleCurrency} onValueChange={setSaleCurrency}>
                    <SelectTrigger className="mt-1" data-testid="mov-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="MXN">MXN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider">Nota</Label>
            <Textarea value={note} onChange={(e)=>setNote(e.target.value)} rows={2} data-testid="mov-note" className="mt-1" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="mov-cancel">Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="mov-submit">
              {saving ? "Guardando…" : "Registrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
